import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell from "../../components/layout/AuthShell";
import { Button } from "../../components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../components/ui/input-otp";
import api from "../../lib/api";
import { useAuth, formatApiError, STAFF_ROLES } from "../../context/AuthContext";

export default function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applyAuth } = useAuth();
  const [email] = useState(location.state?.email || "");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      applyAuth(data);
      toast.success("Email verified — welcome!");
      navigate(STAFF_ROLES.includes(data.user.role) ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-otp", { email });
      toast.success("A new verification code has been sent to your email.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!email) {
    return (
      <AuthShell title="Verify Email" subtitle="We need your email to verify.">
        <p className="text-muted-foreground">Please <Link to="/register" className="text-gold hover:underline">register</Link> first.</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Verify Your Email" subtitle={`Enter the 6-digit code sent to ${email}.`}>
      <form onSubmit={submit} className="space-y-6" data-testid="verify-otp-form">
        <div className="flex justify-center">
          <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} className="border-white/20 text-white text-lg" />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>

        {error && <p className="text-sm text-firered text-center" data-testid="otp-error">{error}</p>}

        <Button type="submit" disabled={loading || otp.length !== 6} className="w-full cf-gold-btn font-semibold h-11" data-testid="verify-otp-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Continue"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Didn't get a code?{" "}
        <button onClick={resend} className="text-gold hover:underline" data-testid="resend-otp-btn">Resend</button>
      </p>
    </AuthShell>
  );
}
